import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BrandIcon from '../../components/BrandIcon';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import api from '../../services/api';
import toast from 'react-hot-toast';

const P13 = 'P13';
const DEFAULT_LOCATION = { lat: -27.5969, lng: -48.5495 };

function NearbyMap({ drivers }) {
  const mapRef = React.useRef(null);
  const mapObj = React.useRef(null);
  const markersRef = React.useRef([]);

  useEffect(() => {
    if (!window.L || !mapRef.current || mapObj.current) return;
    const L = window.L;
    const map = L.map(mapRef.current, { zoomControl: false }).setView([DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng], 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap' }).addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    mapObj.current = map;
  }, []);

  useEffect(() => {
    if (window.L) return;
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    document.head.appendChild(script);
    return () => {};
  }, []);

  useEffect(() => {
    if (!mapObj.current || !window.L) return;
    const L = window.L;
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    drivers.forEach(driver => {
      const icon = L.divIcon({
        html: `<div style="width:16px;height:16px;border-radius:999px;background:${driver.color};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.25)"></div>`,
        className: '',
        iconAnchor: [8, 8],
      });
      const marker = L.marker([driver.location.lat, driver.location.lng], { icon })
        .bindPopup(`${driver.name}<br/>${driver.brandName || 'Entregador'}`)
        .addTo(mapObj.current);
      markersRef.current.push(marker);
    });
  }, [drivers]);

  return <div ref={mapRef} className="w-full h-full" />;
}

export default function CustomerHome() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [brands, setBrands] = useState([]);
  const [step, setStep] = useState(1); // 1=marca, 2=confirmar
  const [selected, setSelected] = useState({ brand: null, qty: 1, address: '', addressStreet: '', addressNumber: '', addressComplement: '', addressNeighborhood: '', addressCity: '', lat: null, lng: null });
  const [loading, setLoading] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [savingAddress, setSavingAddress] = useState(false);
  const [drivers, setDrivers] = useState([]);

const DEFAULT_BRANDS = [
    { id: 'liquigas', name: 'Liquigás', color: '#9CA3AF', sizes: [{ size: P13, customerPrice: 139 }] },
    { id: 'ultragaz', name: 'Ultragaz', color: '#1E40AF', sizes: [{ size: P13, customerPrice: 139 }] },
    { id: 'supergasbras', name: 'Supergasbras', color: '#D97706', sizes: [{ size: P13, customerPrice: 139 }] },
    { id: 'nacional_gas', name: 'Nacional Gás', color: '#C0C0C0', sizes: [{ size: P13, customerPrice: 139 }] },
    { id: 'consigaz', name: 'Consigaz', color: '#38BDF8', sizes: [{ size: P13, customerPrice: 139 }] },
  ];

  useEffect(() => {
    api.get('/brands').then(r => setBrands(r.data)).catch(() => setBrands(DEFAULT_BRANDS));
    api.get('/customer/loyalty').then(r => setLoyalty(r.data)).catch(() => {});
    api.get('/customer/addresses').then(r => setAddresses(r.data)).catch(() => {});
    api.get('/delivery/nearby').then(r => setDrivers(r.data)).catch(() => {});

    // Solicitar localização do cliente
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setSelected(p => ({ ...p, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        },
        (err) => { console.log('Geolocation denied or unavailable', err.code); }
      );
    }
  }, []);

  const anyBrandOption = {
    id: 'any_brand',
    name: 'Qualquer marca',
    color: '#F97316',
    sizes: [{ size: P13, customerPrice: 139 }],
  };
  const brandOptions = [anyBrandOption, ...brands];
  const selBrand = brands.find(b => b.id === selected.brand);
  const activeBrand = selected.brand === 'any_brand' ? anyBrandOption : selBrand;
  const p13 = activeBrand?.sizes.find(s => s.size === P13);
  const cashbackAvailable = (loyalty?.cashback || 0);
  const subtotal = p13 ? p13.customerPrice * selected.qty : 0;
  const cashbackApplied = selected.useCashback ? Math.min(cashbackAvailable, subtotal) : 0;
  const totalToPay = Math.max(0, subtotal - cashbackApplied);

  const createOrder = async () => {
    if (!selected.address.trim()) { toast.error('Informe o endereço de entrega'); return; }
    setLoading(true);
    try {
      const r = await api.post('/orders', {
        brandId: selected.brand,
        size: P13,
        quantity: selected.qty,
        address: selected.address,
        addressStreet: selected.addressStreet,
        addressNumber: selected.addressNumber,
        addressComplement: selected.addressComplement,
        addressNeighborhood: selected.addressNeighborhood,
        addressCity: selected.addressCity,
        customerLat: selected.lat || DEFAULT_LOCATION.lat,
        customerLng: selected.lng || DEFAULT_LOCATION.lng,
        paymentMethod: selected.paymentMethod || 'pix',
        useCashback: selected.useCashback || false,
      });
      nav(`/cliente/pagamento/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao criar pedido');
    } finally {
      setLoading(false);
    }
  };

  const saveCurrentAddress = async () => {
    if (!selected.address.trim()) {
      toast.error('Digite o endereço antes de salvar');
      return;
    }
    setSavingAddress(true);
    try {
      const label = `Endereço ${addresses.length + 1}`;
      const response = await api.post('/customer/addresses', {
        label,
        address: selected.address,
        location: DEFAULT_LOCATION,
      });
      setAddresses(response.data);
      toast.success('Endereço salvo');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar endereço');
    } finally {
      setSavingAddress(false);
    }
  };

  const driverMarkers = drivers.map(driver => {
    const brand = brands.find(item => item.id === driver.primaryBrandId);
    return {
      ...driver,
      color: brand?.color || '#f97316',
      brandName: brand?.name || '',
    };
  });

  return (
    <div className="screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="gradient-hero px-6 pt-10 pb-7 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex justify-center mb-3">
            <img src="/logo.png" alt="GasNaPorta" className="h-20 w-auto object-contain logo-glow" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sky-300 text-[10px] uppercase tracking-[0.3em] font-black">Cliente</p>
              <h2 className="font-black text-lg leading-tight mt-0.5">Olá, {user?.name?.split(' ')[0]} 👋</h2>
            </div>
            <div className="flex gap-3 items-center">
              <button onClick={() => nav('/cliente/carteira')} className="text-sky-200 hover:text-white text-xs font-semibold">Carteira</button>
              <button onClick={() => nav('/cliente/historico')} className="text-sky-200 hover:text-white text-xs font-semibold">Pedidos</button>
              <button onClick={logout} className="text-sky-200 hover:text-white text-xs font-semibold">Sair</button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 py-5 overflow-y-auto fade-in">
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm mb-5">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <div>
              <div className="font-black text-gray-800">Entregadores por perto</div>
              <div className="text-xs text-gray-500">Cores por marca da revenda</div>
            </div>
            <div className="text-xs font-bold text-primary">{driverMarkers.length} online</div>
          </div>
          <div className="h-52 bg-gray-100">
            <NearbyMap drivers={driverMarkers} />
          </div>
        </div>

        {loyalty && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-orange-100 rounded-2xl p-4 mb-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-bold text-orange-500 uppercase tracking-wide">Clube Fidelidade</div>
                <div className="text-sm font-black text-gray-800">Saldo cashback: R$ {(loyalty.cashback || 0).toFixed(2)}</div>
                <div className="text-xs text-gray-500 mt-1">Pontos: {loyalty.ordersCount || 0} · faltam {loyalty.nextFreeAt || 10} pedido(s) para ganhar R$ {(loyalty.discount || 5).toFixed(2)}</div>
              </div>
              <button onClick={() => nav('/cliente/carteira')} className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-xl shadow-sm">💰</button>
            </div>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {['Marca', 'Confirmar'].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${step > i + 1 ? 'text-green-600' : step === i + 1 ? 'text-primary' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step > i + 1 ? 'bg-green-600 text-white' : step === i + 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-400'}`}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                {s}
              </div>
              {i < 1 && <div className="flex-1 h-px bg-gray-200" />}
            </React.Fragment>
          ))}
        </div>

        {/* ── STEP 1: Escolha da Marca ── */}
        {step === 1 && (
          <>
            <h3 className="font-black text-gray-800 text-lg mb-1">Escolha a Marca</h3>
            <p className="text-xs text-gray-500 mb-4">Botijão P13 · 13 kg</p>

            <div className="grid grid-cols-2 gap-3">
              {brandOptions.map(b => {
                const price = b.sizes?.find(s => s.size === P13)?.customerPrice;
                return (
                  <button
                    key={b.id}
                    onClick={() => { setSelected(p => ({ ...p, brand: b.id })); setStep(2); }}
                    className="bg-white rounded-2xl pt-6 pb-4 px-3 flex flex-col items-center shadow-sm border-2 border-transparent hover:border-primary active:scale-95 transition-all"
                  >
                    {/* Mascote colorido */}
                    <div className="mb-3 drop-shadow-lg">
                      {b.id === 'any_brand' ? (
                        <div className="w-[72px] h-[72px] rounded-full bg-orange-100 text-primary flex items-center justify-center text-3xl font-black">+</div>
                      ) : (
                        <BrandIcon brandId={b.id} size={72} />
                      )}
                    </div>
                    <div className="font-black text-gray-800 text-sm">{b.name}</div>
                    {price != null && (
                      <div
                        className="text-xs font-bold mt-1.5 px-3 py-0.5 rounded-full"
                        style={{ backgroundColor: `${b.color}25`, color: b.color }}
                      >
                        R$ {price.toFixed(2)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* ── STEP 2: Confirmar pedido ── */}
        {step === 2 && activeBrand && p13 && (
          <>
            <button onClick={() => setStep(1)} className="text-primary text-sm font-semibold mb-4 block">← Voltar</button>
            <h3 className="font-black text-gray-800 text-lg mb-4">Confirmar Pedido</h3>

            {/* Card da marca */}
            <div className="bg-white rounded-2xl p-4 shadow-sm mb-4 flex items-center gap-4">
              {activeBrand.id === 'any_brand' ? (
                <div className="w-14 h-14 rounded-full bg-orange-100 text-primary flex items-center justify-center text-2xl font-black">+</div>
              ) : (
                <BrandIcon brandId={activeBrand.id} size={56} />
              )}
              <div className="flex-1">
                <div className="font-black text-gray-800">{activeBrand.name}</div>
                <div className="text-sm text-gray-500">Botijão P13 · 13 kg</div>
              </div>
              <div className="text-primary font-black text-xl">
                R$ {(p13.customerPrice * selected.qty).toFixed(2)}
              </div>
            </div>

            {/* Quantidade */}
            <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
              <label className="block text-sm font-semibold text-gray-600 mb-3">Quantidade</label>
              <div className="flex items-center gap-5">
                <button
                  onClick={() => setSelected(p => ({ ...p, qty: Math.max(1, p.qty - 1) }))}
                  className="w-11 h-11 rounded-full bg-gray-100 font-bold text-2xl active:scale-90 transition-all flex items-center justify-center"
                >−</button>
                <span className="text-3xl font-black w-8 text-center">{selected.qty}</span>
                <button
                  onClick={() => setSelected(p => ({ ...p, qty: p.qty + 1 }))}
                  className="w-11 h-11 rounded-full bg-primary text-white font-bold text-2xl active:scale-90 transition-all flex items-center justify-center"
                >+</button>
              </div>
            </div>

            {/* Endereço */}
            <div className="bg-white rounded-2xl p-4 shadow-sm mb-5">
              <label className="block text-sm font-semibold text-gray-600 mb-2">Endereço de entrega</label>
              {addresses.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                  {addresses.map(item => (
                    <button
                      key={item.id}
                      onClick={() => setSelected(p => ({ ...p, address: item.address }))}
                      className="shrink-0 px-3 py-2 rounded-full bg-gray-100 text-xs font-semibold text-gray-700"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
              <AddressAutocomplete
                value={selected.address}
                onChange={val => setSelected(p => ({ ...p, address: val }))}
                onSelect={data => setSelected(p => ({
                  ...p,
                  address: data.fullAddress,
                  addressStreet: data.street,
                  addressNumber: data.number,
                  addressNeighborhood: data.neighborhood,
                  addressCity: data.city,
                  addressComplement: data.complement,
                  lat: data.lat,
                  lng: data.lng,
                }))}
              />
              <button
                onClick={saveCurrentAddress}
                disabled={savingAddress}
                className="mt-3 text-sm font-bold text-primary"
              >
                {savingAddress ? 'Salvando...' : '+ Salvar endereço'}
              </button>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
              <label className="block text-sm font-semibold text-gray-600 mb-3">Forma de pagamento</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'wallet', label: 'Saldo', extra: `R$ ${(loyalty?.cashback || 0).toFixed(2)}` },
                  { id: 'pix', label: 'PIX', extra: '+ R$ 3 cashback' },
                  { id: 'debit', label: 'Débito', extra: '+ R$ 1,50' },
                  { id: 'card', label: 'Crédito', extra: 'sem cashback' },
                ].map(item => (
                  <button
                    key={item.id}
                    onClick={() => setSelected(p => ({ ...p, paymentMethod: item.id }))}
                    className={`rounded-xl border-2 p-3 text-left transition-all ${((selected.paymentMethod || 'pix') === item.id) ? 'border-primary bg-orange-50' : 'border-gray-200'}`}
                  >
                    <div className="font-bold text-sm text-gray-800">{item.label}</div>
                    <div className="text-[11px] text-gray-500 mt-1">{item.extra}</div>
                  </button>
                ))}
              </div>
            </div>

            {cashbackAvailable > 0 && (
              <button
                onClick={() => setSelected(p => ({ ...p, useCashback: !p.useCashback }))}
                className={`w-full mb-4 rounded-2xl p-4 border-2 text-left transition-all ${selected.useCashback ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-gray-800">Usar cashback disponível</div>
                    <div className="text-xs text-gray-500">Saldo atual: R$ {cashbackAvailable.toFixed(2)}</div>
                  </div>
                  <div className={`w-12 h-7 rounded-full relative ${selected.useCashback ? 'bg-green-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${selected.useCashback ? 'left-6' : 'left-1'}`} />
                  </div>
                </div>
              </button>
            )}

            {/* Resumo */}
            <div className="bg-orange-50 rounded-2xl p-4 mb-5 border border-orange-100">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{selected.qty}x Botijão P13</span>
                <span>R$ {subtotal.toFixed(2)}</span>
              </div>
              {selected.useCashback && cashbackApplied > 0 && (
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Cashback aplicado</span>
                  <span className="text-green-600 font-semibold">- R$ {cashbackApplied.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Pagamento</span>
                <span className="font-semibold text-gray-700">
                  {selected.paymentMethod === 'wallet'
                    ? 'Saldo em carteira'
                    : selected.paymentMethod === 'debit'
                      ? 'Débito'
                      : selected.paymentMethod === 'card'
                        ? 'Crédito'
                        : 'PIX'}
                </span>
              </div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Taxa de entrega</span>
                <span className="text-green-600 font-semibold">Incluída</span>
              </div>
              <div className="border-t border-orange-200 pt-2 mt-2 flex justify-between font-black text-primary text-lg">
                <span>Total</span>
                <span>R$ {totalToPay.toFixed(2)}</span>
              </div>
            </div>

            <button
              onClick={createOrder}
              disabled={loading}
              className="w-full py-4 bg-primary hover:bg-primary-dark disabled:opacity-60 rounded-2xl font-bold text-white text-lg transition-all active:scale-95 shadow-lg shadow-orange-500/30"
            >
              {loading ? 'Processando...' : 'Ir para Pagamento →'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
